import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit, EyeOff, Globe, ImagePlus, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, adminFileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

function richTextPlainText(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return doc.body.textContent.replace(/\s+/g, " ").trim();
}

const emptyForm = {
  title: "",
  category: "",
  tags: "",
  summary: "",
  body: "",
  publication_date: new Date().toISOString().slice(0, 10),
  status: "draft",
  featured_image_path: "",
  seo_title: "",
  seo_description: "",
  canonical_url: "",
  og_image_path: "",
};

export default function AdminInsightsPage() {
  const { user, logout } = useAuth();
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [ogFile, setOgFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const loadArticles = () => api.get("/admin/articles").then((response) => setArticles(response.data)).catch((error) => toast.error(formatApiError(error, "Unable to load articles.")));
  useEffect(() => { loadArticles(); }, []);

  const updateField = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const resetForm = () => { setForm(emptyForm); setFile(null); setOgFile(null); setEditingId(null); setFormError(""); };

  const selectImage = (setter, label) => (event) => {
    const selected = event.target.files?.[0] || null;
    setFormError("");
    if (!selected) {
      setter(null);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(selected.type)) {
      setter(null);
      setFormError(`${label} must be a JPEG, PNG, WebP or GIF.`);
      event.target.value = "";
      return;
    }
    if (selected.size > MAX_IMAGE_BYTES) {
      setter(null);
      setFormError(`${label} must be 5 MB or smaller.`);
      event.target.value = "";
      return;
    }
    setter(selected);
  };

  const editArticle = async (article) => {
    try {
      const response = await api.get(`/admin/articles/${article.id}`);
      const fullArticle = response.data;
      setEditingId(fullArticle.id);
      setFile(null);
      setOgFile(null);
      setFormError("");
      setForm({
        title: fullArticle.title,
        category: fullArticle.category,
        tags: (fullArticle.tags || []).join(", "),
        summary: fullArticle.summary,
        body: fullArticle.body,
        publication_date: fullArticle.publication_date,
        status: fullArticle.status,
        featured_image_path: fullArticle.featured_image_path,
        seo_title: fullArticle.seo_title || "",
        seo_description: fullArticle.seo_description || "",
        canonical_url: fullArticle.canonical_url || "",
        og_image_path: fullArticle.og_image_path || "",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error(formatApiError(error, "Unable to load the article for editing."));
    }
  };

  const submitArticle = async (event) => {
    event.preventDefault();
    setFormError("");
    if (!editingId && !file) {
      setFormError("Upload a featured image before saving the article.");
      document.querySelector('[data-testid="article-image-upload-label"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (richTextPlainText(form.body).length < 20) {
      setFormError("Article body must contain at least 20 characters.");
      document.querySelector('[data-testid="article-rich-text-editor"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    const status = event.nativeEvent.submitter?.value || form.status;
    setForm({ ...form, status });
    const data = new FormData();
    ["title", "category", "tags", "summary", "body", "publication_date", "seo_title", "seo_description", "canonical_url"].forEach((key) => data.append(key, form[key]));
    data.append("status", status);
    if (file) data.append("featured_image", file);
    if (ogFile) data.append("og_image", ogFile);
    try {
      if (editingId) await api.put(`/admin/articles/${editingId}`, data);
      else await api.post("/admin/articles", data);
      toast.success(editingId ? "Article updated." : "Article created.");
      resetForm();
      await loadArticles();
    } catch (error) {
      const message = formatApiError(error, "Unable to save the article.");
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (article, status) => {
    await api.post(`/admin/articles/${article.id}/${status}`);
    toast.success(status === "publish" ? "Article published." : "Article unpublished.");
    await loadArticles();
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete "${article.title}"?`)) return;
    await api.delete(`/admin/articles/${article.id}`);
    toast.success("Article deleted.");
    if (editingId === article.id) resetForm();
    await loadArticles();
  };

  return (
    <main data-testid="admin-insights-dashboard" className="min-h-screen bg-[#FDFBF7] text-[#1C1613]">
      <header className="border-b border-[#E5DFD3] bg-[#FDFBF7]">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <Link to="/" className="flex items-center gap-4">
            <img src="/assets/ugi-official-logo-web.png" alt="UGI" className="h-14 w-auto object-contain" />
            <span className="text-xs font-bold uppercase tracking-[0.24em] text-[#9B7437]">Editorial Admin</span>
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            {user?.is_temporary && <span data-testid="temporary-admin-notice" className="border border-[#C5A059]/40 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8E6A2F]">Temporary test admin</span>}
            <Link data-testid="admin-view-site-link" to="/insights" className="border border-[#1C1613]/20 px-4 py-3 text-xs font-bold tracking-[0.14em]">VIEW INSIGHTS</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="bg-[#1C1613] px-4 py-3 text-xs font-bold tracking-[0.14em] text-[#FDFBF7]">LOG OUT</button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-[0.95fr_1.05fr]">
        <section className="border border-[#E5DFD3] bg-white p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#C5A059]">{editingId ? "Edit Article" : "New Article"}</p>
              <h1 className="mt-3 font-serif text-4xl font-normal">UGI Insights</h1>
            </div>
            {editingId && <button data-testid="cancel-edit-button" onClick={resetForm} className="border border-[#1C1613]/20 px-3 py-2 text-xs font-bold">CANCEL</button>}
          </div>
          <form data-testid="article-editor-form" onSubmit={submitArticle} className="mt-8 grid gap-5">
            <input data-testid="article-title-input" name="title" value={form.title} onChange={updateField} required placeholder="Article title" className="h-12 border border-[#D8D0C3] px-4 text-sm" />
            <input data-testid="article-category-input" name="category" value={form.category} onChange={updateField} required placeholder="Category" className="h-12 border border-[#D8D0C3] px-4 text-sm" />
            <input data-testid="article-tags-input" name="tags" value={form.tags} onChange={updateField} placeholder="Tags, separated by commas" className="h-12 border border-[#D8D0C3] px-4 text-sm" />
            <input data-testid="article-date-input" name="publication_date" type="date" value={form.publication_date} onChange={updateField} required className="h-12 border border-[#D8D0C3] px-4 text-sm" />
            <textarea data-testid="article-summary-input" name="summary" value={form.summary} onChange={updateField} required minLength={20} maxLength={400} rows={4} placeholder="Summary" className="border border-[#D8D0C3] p-4 text-sm" />
            <RichTextEditor value={form.body} onChange={(body) => setForm({ ...form, body })} />
            <div className="grid gap-2">
              <label data-testid="article-image-upload-label" className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-[#C5A059]/50 bg-[#F5F2EB] px-4 py-6 text-sm font-semibold text-[#524842]">
                <ImagePlus size={19} strokeWidth={1.5} className="text-[#C5A059]" />
                {file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)` : editingId ? "Replace featured image (optional)" : "Upload featured image"}
                <input data-testid="article-image-input" id="featured-image" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={selectImage(setFile, "Featured image")} aria-describedby="featured-image-help article-form-error" className="sr-only" />
              </label>
              <p id="featured-image-help" className="text-xs leading-5 text-[#8A7D70]">JPEG, PNG, WebP or GIF up to 5 MB. The image uploads when you save or publish.</p>
              {formError && <p id="article-form-error" data-testid="article-form-error" role="alert" aria-live="polite" className="border border-red-800/20 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800">{formError}</p>}
            </div>
            {form.featured_image_path && !file && <img data-testid="article-current-image-preview" src={adminFileUrl(form.featured_image_path)} alt="Current featured" className="aspect-[16/8] w-full object-cover" />}

            <fieldset data-testid="article-seo-section" className="grid gap-4 border border-[#E5DFD3] bg-[#FDFBF7] p-5">
              <legend className="flex items-center gap-2 px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B7437]"><Search size={12} strokeWidth={2} /> SEO &amp; social sharing (optional)</legend>
              <div className="grid gap-1.5">
                <input data-testid="article-seo-title-input" name="seo_title" value={form.seo_title} onChange={updateField} maxLength={180} placeholder="SEO title — defaults to the article title" className="h-12 border border-[#D8D0C3] bg-white px-4 text-sm" />
                <p className="text-xs text-[#8A7D70]">{form.seo_title.length}/180 · shown in search results and the browser tab</p>
              </div>
              <div className="grid gap-1.5">
                <textarea data-testid="article-seo-description-input" name="seo_description" value={form.seo_description} onChange={updateField} maxLength={320} rows={3} placeholder="Meta description — defaults to the article summary" className="border border-[#D8D0C3] bg-white p-4 text-sm" />
                <p className="text-xs text-[#8A7D70]">{form.seo_description.length}/320 · aim for 150–160 characters for search snippets</p>
              </div>
              <div className="grid gap-1.5">
                <input data-testid="article-canonical-url-input" name="canonical_url" type="url" value={form.canonical_url} onChange={updateField} maxLength={500} placeholder="Canonical URL — defaults to this article's public URL" className="h-12 border border-[#D8D0C3] bg-white px-4 text-sm" />
                <p className="text-xs text-[#8A7D70]">Only needed if this article also appears on another website</p>
              </div>
              <div className="grid gap-1.5">
                <label data-testid="article-og-image-upload-label" className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-[#C5A059]/50 bg-white px-4 py-5 text-sm font-semibold text-[#524842]">
                  <ImagePlus size={18} strokeWidth={1.5} className="text-[#C5A059]" />
                  {ogFile ? `${ogFile.name} (${(ogFile.size / 1024 / 1024).toFixed(2)} MB)` : form.og_image_path ? "Replace social share image (optional)" : "Social share image (optional — defaults to the featured image)"}
                  <input data-testid="article-og-image-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={selectImage(setOgFile, "Social share image")} className="sr-only" />
                </label>
                <p className="text-xs text-[#8A7D70]">1200 × 630 px recommended · used when the article link is shared on social media</p>
                {form.og_image_path && !ogFile && <img data-testid="article-og-image-preview" src={adminFileUrl(form.og_image_path)} alt="Current social share" className="aspect-[1200/630] w-full border border-[#E5DFD3] object-cover" />}
              </div>
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <button data-testid="save-draft-button" type="submit" name="statusAction" value="draft" disabled={saving} className="border border-[#1C1613] px-4 py-4 text-xs font-bold tracking-[0.14em]">{saving ? "SAVING..." : "SAVE DRAFT"}</button>
              <button data-testid="save-publish-button" type="submit" name="statusAction" value="published" disabled={saving} className="bg-[#C5A059] px-4 py-4 text-xs font-bold tracking-[0.14em] text-[#1C1613]">{saving ? "PUBLISHING..." : "PUBLISH"}</button>
            </div>
          </form>
        </section>

        <section data-testid="admin-article-list" className="grid content-start gap-4">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-3xl font-normal">Articles</h2>
            <span className="text-xs font-bold tracking-[0.16em] text-[#9B7437]">{articles.length} TOTAL</span>
          </div>
          {articles.length === 0 && <div className="border border-[#E5DFD3] bg-white p-8 text-sm text-[#524842]">No articles yet. Create the first official UGI article when content is ready.</div>}
          {articles.map((article) => (
            <article key={article.id} data-testid={`admin-article-row-${article.slug}`} className="border border-[#E5DFD3] bg-white p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <span className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${article.status === "published" ? "bg-[#C5A059]/20 text-[#8E6A2F]" : "bg-[#E5DFD3] text-[#524842]"}`}>{article.status}</span>
                  <h3 className="mt-3 font-serif text-2xl font-normal">{article.title}</h3>
                  <p className="mt-2 text-sm text-[#524842]">{article.category} · {article.publication_date}{article.tags?.length ? ` · ${article.tags.join(", ")}` : ""}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid={`edit-article-${article.slug}`} onClick={() => editArticle(article)} className="border border-[#1C1613]/20 p-3" aria-label="Edit article"><Edit size={16} /></button>
                  {article.status === "published" ? (
                    <button data-testid={`unpublish-article-${article.slug}`} onClick={() => changeStatus(article, "unpublish")} className="border border-[#1C1613]/20 p-3" aria-label="Unpublish article"><EyeOff size={16} /></button>
                  ) : (
                    <button data-testid={`publish-article-${article.slug}`} onClick={() => changeStatus(article, "publish")} className="border border-[#1C1613]/20 p-3" aria-label="Publish article"><Globe size={16} /></button>
                  )}
                  <button data-testid={`delete-article-${article.slug}`} onClick={() => deleteArticle(article)} className="border border-red-800/20 p-3 text-red-800" aria-label="Delete article"><Trash2 size={16} /></button>
                </div>
              </div>
            </article>
          ))}
          <button data-testid="new-article-button" onClick={resetForm} className="inline-flex w-max items-center gap-3 border border-[#1C1613] px-5 py-3 text-xs font-bold tracking-[0.14em]"><Plus size={16} /> NEW ARTICLE</button>
        </section>
      </div>
    </main>
  );
}
